import React, { useEffect, useState } from 'react';
import { Accordion, Card, Container } from 'react-bootstrap';
import TestExecutionAccordionItem from '../../test-execution/TestExecutionAccordionItem';
import { useNotification } from '../../../../hooks/NotificationContext';
import { getConfigurations } from '../../../../modules/config';
import Loader from '../../../elements/Loader';
import SecondaryHeader from '../../../elements/SecondaryHeader';
import { fetchTestCycle } from '../../../../modules/testcycles';
import Label from '../../../elements/Label';
import { Link } from 'react-router-dom';
import { getAssociatedUsers } from '../../../../modules/project';
import Error from '../../../elements/Error';
import { fetchCurrentExecution } from '../../../../modules/executions';
import { getTestCase } from '../../../../modules/jira';

const SingleTestExecutionComponent = ({
  executionKey,
  projectId,
  testCycleKey,
  associatedTestCaseKey,
}) => {
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const defaultStatus = statuses?.find((status) => status?.is_default);
  const [testCycleData, setTestCycleData] = useState({});
  const [error, setError] = useState(false);
  const [currentExecution, setCurrentExecution] = useState(null);

  const fetchtestCycleData = async () => {
    try {
      const testCycleResponse = await fetchTestCycle(testCycleKey, projectId);
      setTestCycleData(testCycleResponse);
    } catch (error) {
      if (error?.message === 'NOT_FOUND') {
        setError(true);
      } else {
        const errorMessage =
          error?.message || 'Unable to fetch test cycle. Please try again...';
        addNotification('danger', errorMessage);
      }
    }
  };

  const fetchData = async () => {
    try {
      const executionResponse = await fetchCurrentExecution(
        executionKey,
        projectId,
        testCycleData?.id,
        associatedTestCaseKey,
      );
      setCurrentExecution(executionResponse?.data);
      const [statusResponse, testCasesResponse, usersResponse] =
        await Promise.all([
          getConfigurations('testexecutionstatus', projectId),
          getTestCase(associatedTestCaseKey, projectId),
          getAssociatedUsers(projectId),
        ]);

      if (statusResponse) {
        setStatuses(statusResponse.data);
      }

      if (usersResponse) {
        setUsers(usersResponse?.map((user) => user?.user));
      }
      if (testCasesResponse) {
        setSelectedTestCase(testCasesResponse);
      }
    } catch (error) {
      if (error?.message === 'NOT_FOUND') {
        setError(true);
      } else {
        const errorMessage =
          error?.message ||
          'Unable to fetch test execution. Please try again...';
        addNotification('danger', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!executionKey || !projectId || !testCycleKey) return;
    fetchtestCycleData();
  }, [executionKey, projectId, testCycleKey]);

  useEffect(() => {
    if (!testCycleData?.id) return;
    fetchData();
  }, [testCycleData?.id]);

  if (error) {
    return (
      <Error>
        <h2>Execution Not Found</h2>
        <p className="text-center">
          The test execution you're looking for doesn't exist or may have been
          removed.{' '}
          <Link to={`/project/${projectId}/testcycles`}>Click here </Link>to
          view your the test cycles
        </p>
      </Error>
    );
  }

  return loading && !selectedTestCase && !defaultStatus && !currentExecution ? (
    <Loader />
  ) : (
    <Container fluid>
      <SecondaryHeader
        pageTitle={
          <>
            <Link
              title="Click to open in new tab"
              to={`/project/${testCycleData?.gainss_project_id}/testcycles/${testCycleKey}`}>
              {testCycleKey}
            </Link>
            : {testCycleData?.name}
            <span style={{ marginLeft: '10px' }}>
              <Label
                color={testCycleData?.status?.color}
                name={testCycleData?.status?.name}
              />
            </span>
          </>
        }
        breadcrumbs={[
          {
            label: 'Test Cases',
            path: `/project/${projectId}/testcases`,
            clickable: true,
          },
          {
            label: selectedTestCase?.key,
            path: `/project/${projectId}/testcases/${selectedTestCase?.key}`,
            clickable: true,
          },
        ]}
      />
      <Container fluid className="px-0">
        <Card className="shadow-sm p-3 m-2">
          <Accordion activeKey={currentExecution?.id} flush>
            <TestExecutionAccordionItem
              openAccordionKey={currentExecution?.id}
              executionId={currentExecution?.id}
              statuses={statuses}
              defaultStatus={defaultStatus}
              users={users}
              testcaseData={selectedTestCase}
              singlePage={true}
            />
          </Accordion>
        </Card>
      </Container>
    </Container>
  );
};

export default SingleTestExecutionComponent;
